/**
 * Run-scoped progress tracking for extraction requests.
 *
 * Every extraction submitted from a result grid becomes one row here, keyed by
 * its processing run id and fed by `GET /projects/{id}/extraction-runs` — a
 * run-scoped document ledger the API derives from extraction units:
 *
 *   - a document is `succeeded` only once *every* one of its units succeeded;
 *     a mix of successes and failures inside one document is `partial`, never
 *     complete;
 *   - counts advance while the run is still in flight, so progress is genuinely
 *     progressive rather than a single jump when the run settles;
 *   - unit counts expose completed matrix sections within each document without
 *     estimating progress inside a running extraction unit.
 *
 * Concurrency is the default case: two extractions from the same grid, or from
 * different grids in the same project, are two rows that poll and settle
 * independently. Nothing is merged into a single percentage.
 *
 * Recovery is a server read, not browser memory. The endpoint enumerates every
 * project run still in flight plus every settled run the viewing user has not
 * acknowledged, so a reload — or a jump to the Documents page — restores all of
 * them, not just whatever the grid's head revision happens to point at.
 *
 * Acknowledgement is durable and server-side. Every settled run — success,
 * failure, partial or cancellation — stays until the reviewer dismisses it, and
 * a dismissal that does not reach the server puts the row back rather than
 * pretending it was handled.
 *
 * The store owns polling rather than a React hook, so progress survives moving
 * between the source grid and the Documents page, and every surface watching
 * the same project shares one poll. Polling runs only while a run is in flight;
 * a settled ledger costs nothing until something starts again.
 */
import { ApiClientError } from '@contract-analyzer/api-client';
import type {
  ExtractionRunDocumentCountsResponse,
  ExtractionRunPhase as ExtractionRunPhaseResponse,
  ExtractionRunProgressResponse,
} from '@contract-analyzer/api-client';

import { apiClient } from '@/lib/api';

export type ExtractionRunPhase = ExtractionRunPhaseResponse;

export interface ExtractionRun {
  readonly runId: string;
  readonly projectId: string;
  readonly analysisId: string | null;
  /** How the user names the grid this extraction was launched from. */
  readonly gridLabel: string;
  /** Canonical template lens, used to link back to the source grid. */
  readonly templateId: string | null;
  readonly phase: ExtractionRunPhase;
  /** Matrix sections in scope; 0 for a whole-document extraction. */
  readonly sectionCount: number;
  readonly counts: ExtractionRunDocumentCountsResponse;
  readonly unitCounts: ExtractionRunProgressResponse['unit_counts'];
  readonly failureMessage: string | null;
  /** Set when a dismissal could not be recorded, so the row came back. */
  readonly acknowledgementError: string | null;
}

export interface ExtractionRunProjectState {
  readonly runs: readonly ExtractionRun[];
  /** Progress reads keep failing. The extractions themselves are unaffected. */
  readonly progressUnavailable: boolean;
}

interface ExtractionRunWatcherScope {
  /** Auth + tenant identity. Prevents protected state crossing viewer sessions. */
  readonly viewerKey: string;
}

/** The narrow surface this store needs; the app binds it to the generated client. */
export interface ExtractionRunProgressReader {
  readonly listRuns: (projectId: string) => Promise<readonly ExtractionRunProgressResponse[]>;
  readonly acknowledgeRun: (projectId: string, runId: string) => Promise<void>;
}

const apiReader: ExtractionRunProgressReader = {
  async listRuns(projectId) {
    const response = await apiClient.listExtractionRunProgress(projectId);
    return response.runs;
  },
  async acknowledgeRun(projectId, runId) {
    await apiClient.acknowledgeExtractionRun(projectId, runId);
  },
};

export interface ExtractionRunStoreOptions {
  readonly reader?: ExtractionRunProgressReader;
  /** Cadence while at least one run is still in flight. */
  readonly activePollIntervalMs?: number;
}

/**
 * What a reset may rebind. A surface test drives the process-wide store through
 * a faithful reader of its own instead of reaching past this boundary.
 */
interface ExtractionRunStoreResetOverrides {
  readonly reader?: ExtractionRunProgressReader;
}

export interface ExtractionRunStore {
  subscribe: (listener: () => void) => () => void;
  getProjectState: (projectId: string, viewerKey: string) => ExtractionRunProjectState;
  /** Register a surface that displays this project. Returns an unwatch function. */
  watch: (projectId: string, scope: ExtractionRunWatcherScope) => () => void;
  /** Read the ledger now — after a submission, or whenever a surface has reason to. */
  refresh: (projectId: string) => void;
  /** Durably acknowledge a settled run. Restores the row if the write fails. */
  acknowledge: (projectId: string, runId: string) => void;
  /** Acknowledge every succeeded run at once; other outcomes still need a look each. */
  acknowledgeSucceeded: (projectId: string) => void;
  /** Stop repeating the "progress unavailable" notice and try reading again. */
  dismissProgressNotice: (projectId: string) => void;
  /** Test seam: drop every record, cancel every timer, and rebind the reader. */
  reset: (overrides?: ExtractionRunStoreResetOverrides) => void;
}

const DEFAULT_ACTIVE_POLL_MS = 2_000;
const MAX_CONSECUTIVE_READ_FAILURES = 5;
const RETRY_BACKOFF_MS = [2_000, 4_000, 8_000] as const;
const ACKNOWLEDGEMENT_FAILED_MESSAGE = 'This could not be dismissed. Try again.';
const UNNAMED_GRID_LABEL = 'Result grid';

const EMPTY_STATE: ExtractionRunProjectState = Object.freeze({
  runs: Object.freeze([]),
  progressUnavailable: false,
});

const TERMINAL_PHASES: ReadonlySet<ExtractionRunPhase> = new Set<ExtractionRunPhase>([
  'succeeded',
  'partial',
  'failed',
  'cancelled',
]);

export function isTerminalExtractionRunPhase(phase: ExtractionRunPhase): boolean {
  return TERMINAL_PHASES.has(phase);
}

/**
 * Reading progress needs the same permission that starts an extraction. A
 * caller who has lost it is not experiencing an outage, so the surface goes
 * quiet instead of nagging them about a capability they do not hold. A 404 is
 * deliberately not in this set: it is what an API image behind the web image
 * answers during a deploy, and that must read as a transient failure.
 */
function isAccessLoss(cause: unknown): boolean {
  return cause instanceof ApiClientError && (cause.status === 401 || cause.status === 403);
}

interface ProjectMonitor {
  viewerKey: string | null;
  watchers: number;
  timer: ReturnType<typeof setTimeout> | null;
  inFlight: boolean;
  consecutiveFailures: number;
  noticeDismissed: boolean;
  /** The caller cannot read this project's progress; stop asking. */
  outOfScope: boolean;
  /** Runs the server has confirmed as acknowledged; hidden until it catches up. */
  readonly acknowledged: Set<string>;
  /** Runs with an acknowledgement request in flight; hidden meanwhile. */
  readonly pendingAcknowledgements: Set<string>;
  /** Runs whose last dismissal failed; shown with a notice until retried. */
  readonly acknowledgementFailures: Set<string>;
}

export function createExtractionRunStore(
  options: ExtractionRunStoreOptions = {},
): ExtractionRunStore {
  let reader = options.reader ?? apiReader;
  const activePollMs = options.activePollIntervalMs ?? DEFAULT_ACTIVE_POLL_MS;

  const listeners = new Set<() => void>();
  const monitors = new Map<string, ProjectMonitor>();
  const states = new Map<string, ExtractionRunProjectState>();
  let visibilityBound = false;

  function emit(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function readState(projectId: string): ExtractionRunProjectState {
    return states.get(projectId) ?? EMPTY_STATE;
  }

  function writeState(projectId: string, next: ExtractionRunProjectState): void {
    states.set(projectId, next);
    emit();
  }

  function setRuns(projectId: string, runs: readonly ExtractionRun[]): void {
    writeState(projectId, { ...readState(projectId), runs });
  }

  function clearTimer(monitor: ProjectMonitor): void {
    if (monitor.timer !== null) {
      clearTimeout(monitor.timer);
      monitor.timer = null;
    }
  }

  function createMonitor(viewerKey: string | null): ProjectMonitor {
    return {
      viewerKey,
      watchers: 0,
      timer: null,
      inFlight: false,
      consecutiveFailures: 0,
      noticeDismissed: false,
      outOfScope: false,
      acknowledged: new Set<string>(),
      pendingAcknowledgements: new Set<string>(),
      acknowledgementFailures: new Set<string>(),
    };
  }

  /**
   * The monitor for a project, replaced when a different viewer takes over or
   * when a new watcher arrives after access was lost — the new session may
   * hold the permission the old one did not.
   */
  function ensureMonitor(projectId: string, viewerKey: string | null): ProjectMonitor {
    const existing = monitors.get(projectId);
    if (existing !== undefined) {
      const viewerChanged =
        viewerKey !== null && existing.viewerKey !== null && existing.viewerKey !== viewerKey;
      const revived = viewerKey !== null && existing.outOfScope;
      if (!viewerChanged && !revived) {
        if (viewerKey !== null) {
          existing.viewerKey = viewerKey;
        }
        return existing;
      }
      clearTimer(existing);
      monitors.delete(projectId);
      states.delete(projectId);
      emit();
    }
    const monitor = createMonitor(viewerKey);
    monitors.set(projectId, monitor);
    return monitor;
  }

  function totalWatchers(): number {
    let total = 0;
    for (const monitor of monitors.values()) {
      total += monitor.watchers;
    }
    return total;
  }

  function refreshWatchedProjects(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    for (const [projectId, monitor] of monitors) {
      if (monitor.watchers > 0) {
        void refresh(projectId);
      }
    }
  }

  function bindVisibility(): void {
    if (visibilityBound || typeof document === 'undefined') {
      return;
    }
    document.addEventListener('visibilitychange', refreshWatchedProjects);
    visibilityBound = true;
  }

  function unbindVisibility(): void {
    if (!visibilityBound || typeof document === 'undefined') {
      return;
    }
    document.removeEventListener('visibilitychange', refreshWatchedProjects);
    visibilityBound = false;
  }

  /**
   * When to read again, or `null` to stop: a settled ledger is not polled, and
   * an outage is retried with backoff until the notice is shown, after which
   * the reviewer's dismissal (or a tab becoming visible again) restarts it.
   */
  function nextDelayMs(projectId: string, monitor: ProjectMonitor): number | null {
    if (monitor.consecutiveFailures >= MAX_CONSECUTIVE_READ_FAILURES) {
      return null;
    }
    if (monitor.consecutiveFailures > 0) {
      const index = Math.min(monitor.consecutiveFailures - 1, RETRY_BACKOFF_MS.length - 1);
      return RETRY_BACKOFF_MS[index] ?? activePollMs;
    }
    const hasActiveRun = readState(projectId).runs.some(
      (run) => !isTerminalExtractionRunPhase(run.phase),
    );
    return hasActiveRun ? activePollMs : null;
  }

  function scheduleNext(projectId: string, monitor: ProjectMonitor): void {
    clearTimer(monitor);
    if (monitor.watchers === 0 || monitor.outOfScope) {
      return;
    }
    const delay = nextDelayMs(projectId, monitor);
    if (delay === null) {
      return;
    }
    monitor.timer = setTimeout(() => {
      monitor.timer = null;
      void refresh(projectId);
    }, delay);
  }

  function toRun(monitor: ProjectMonitor, response: ExtractionRunProgressResponse): ExtractionRun {
    return {
      runId: response.run_id,
      projectId: response.project_id,
      analysisId: response.analysis_id,
      gridLabel: response.analysis_name ?? UNNAMED_GRID_LABEL,
      templateId: response.template_id,
      phase: response.phase,
      sectionCount: response.section_count,
      counts: response.counts,
      unitCounts: response.unit_counts,
      failureMessage: response.failure_message,
      acknowledgementError: monitor.acknowledgementFailures.has(response.run_id)
        ? ACKNOWLEDGEMENT_FAILED_MESSAGE
        : null,
    };
  }

  function applyServerRuns(
    projectId: string,
    monitor: ProjectMonitor,
    responses: readonly ExtractionRunProgressResponse[],
  ): void {
    const reported = new Set(responses.map((response) => response.run_id));
    for (const runId of monitor.acknowledged) {
      if (!reported.has(runId)) {
        monitor.acknowledged.delete(runId);
      }
    }
    for (const runId of monitor.acknowledgementFailures) {
      if (!reported.has(runId)) {
        monitor.acknowledgementFailures.delete(runId);
      }
    }
    const runs = responses
      .filter(
        (response) =>
          !monitor.acknowledged.has(response.run_id) &&
          !monitor.pendingAcknowledgements.has(response.run_id),
      )
      .map((response) => toRun(monitor, response));
    writeState(projectId, { runs, progressUnavailable: false });
    monitor.noticeDismissed = false;
  }

  function stopForAccessLoss(projectId: string, monitor: ProjectMonitor): void {
    clearTimer(monitor);
    monitor.pendingAcknowledgements.clear();
    monitor.acknowledged.clear();
    monitor.acknowledgementFailures.clear();
    monitor.outOfScope = true;
    states.delete(projectId);
    emit();
  }

  async function refresh(projectId: string): Promise<void> {
    const monitor = monitors.get(projectId);
    if (
      monitor === undefined ||
      monitor.watchers === 0 ||
      monitor.inFlight ||
      monitor.outOfScope
    ) {
      return;
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      // The visibility listener resumes the poll when the tab is looked at again.
      clearTimer(monitor);
      return;
    }
    monitor.inFlight = true;
    try {
      const responses = await reader.listRuns(projectId);
      if (monitors.get(projectId) !== monitor) {
        return;
      }
      monitor.consecutiveFailures = 0;
      applyServerRuns(projectId, monitor, responses);
    } catch (error) {
      if (monitors.get(projectId) !== monitor) {
        return;
      }
      if (isAccessLoss(error)) {
        stopForAccessLoss(projectId, monitor);
        return;
      }
      monitor.consecutiveFailures += 1;
      const unavailable = monitor.consecutiveFailures >= MAX_CONSECUTIVE_READ_FAILURES;
      if (unavailable && !monitor.noticeDismissed) {
        writeState(projectId, { ...readState(projectId), progressUnavailable: true });
      }
    } finally {
      if (monitors.get(projectId) === monitor) {
        monitor.inFlight = false;
        scheduleNext(projectId, monitor);
      }
    }
  }

  function acknowledgeRun(projectId: string, runId: string): void {
    const monitor = monitors.get(projectId);
    if (monitor === undefined || monitor.outOfScope) {
      return;
    }
    const current = readState(projectId).runs;
    if (
      !current.some((run) => run.runId === runId) ||
      monitor.acknowledged.has(runId) ||
      monitor.pendingAcknowledgements.has(runId)
    ) {
      return;
    }
    monitor.pendingAcknowledgements.add(runId);
    monitor.acknowledgementFailures.delete(runId);
    setRuns(
      projectId,
      current.filter((run) => run.runId !== runId),
    );
    void (async () => {
      try {
        await reader.acknowledgeRun(projectId, runId);
      } catch (error) {
        if (monitors.get(projectId) !== monitor || monitor.outOfScope) {
          return;
        }
        monitor.pendingAcknowledgements.delete(runId);
        if (isAccessLoss(error)) {
          stopForAccessLoss(projectId, monitor);
          return;
        }
        // The row comes back from the server's ledger, carrying the notice, so
        // the reviewer sees current counts rather than the snapshot they dismissed.
        monitor.acknowledgementFailures.add(runId);
        void refresh(projectId);
        return;
      }
      if (monitors.get(projectId) !== monitor || monitor.outOfScope) {
        return;
      }
      monitor.pendingAcknowledgements.delete(runId);
      monitor.acknowledged.add(runId);
    })();
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getProjectState(projectId, viewerKey) {
      const monitor = monitors.get(projectId);
      if (
        monitor === undefined ||
        monitor.outOfScope ||
        monitor.viewerKey === null ||
        monitor.viewerKey !== viewerKey
      ) {
        return EMPTY_STATE;
      }
      return readState(projectId);
    },
    watch(projectId, scope) {
      const monitor = ensureMonitor(projectId, scope.viewerKey);
      monitor.watchers += 1;
      bindVisibility();
      void refresh(projectId);
      let released = false;
      return () => {
        if (released) {
          return;
        }
        released = true;
        if (monitors.get(projectId) !== monitor) {
          return;
        }
        monitor.watchers -= 1;
        if (monitor.watchers === 0) {
          clearTimer(monitor);
        }
        if (totalWatchers() === 0) {
          unbindVisibility();
        }
      };
    },
    refresh(projectId) {
      const monitor = monitors.get(projectId);
      if (monitor === undefined) {
        return;
      }
      // A read already scheduled for later is superseded by this one.
      clearTimer(monitor);
      void refresh(projectId);
    },
    acknowledge(projectId, runId) {
      acknowledgeRun(projectId, runId);
    },
    acknowledgeSucceeded(projectId) {
      for (const run of readState(projectId).runs) {
        if (run.phase === 'succeeded') {
          acknowledgeRun(projectId, run.runId);
        }
      }
    },
    dismissProgressNotice(projectId) {
      const monitor = monitors.get(projectId);
      if (monitor === undefined) {
        return;
      }
      monitor.noticeDismissed = true;
      monitor.consecutiveFailures = 0;
      writeState(projectId, { ...readState(projectId), progressUnavailable: false });
      void refresh(projectId);
    },
    reset(overrides) {
      for (const monitor of monitors.values()) {
        clearTimer(monitor);
      }
      monitors.clear();
      states.clear();
      unbindVisibility();
      reader = overrides?.reader ?? options.reader ?? apiReader;
      emit();
    },
  };
}

/** Process-wide store: extraction progress outlives any single mounted surface. */
export const extractionRunStore = createExtractionRunStore();
